import { createContext, useContext, type ReactNode } from "react";
import type { DecisionEngine, Channel, Message } from ".";
import type { TagsSchema } from "@hightouch/lib/customer-data/decision-engine/types";

export type DecisionEngineContext = {
  engine: DecisionEngine;
  channels: Array<Channel>;
  messages: Array<Message>;
  activeMessages: Array<Message>;
  messageTagsSchema: TagsSchema;
  variantTagsSchema: TagsSchema;
  channelTagsSchema: TagsSchema;
};
const DecisionEngineCtx = createContext<DecisionEngineContext | null>(null);

export function DecisionEngineProvider({
  value,
  children,
}: {
  value: DecisionEngineContext;
  children: ReactNode;
}) {
  return (
    <DecisionEngineCtx.Provider value={value}>
      {children}
    </DecisionEngineCtx.Provider>
  );
}

export function useDecisionEngineContext(): DecisionEngineContext {
  const ctx = useContext(DecisionEngineCtx);
  if (!ctx) {
    throw new Error(
      "useDecisionEngineContext must be used within a DecisionEngineProvider",
    );
  }
  return ctx;
}

/** Engine-wide collections retain this representative-parent lookup; channels use their explicit assignment. */
export function useDecisionEngineParentModel(): DecisionEngine["flows"][number]["parent_model"] {
  const { engine } = useDecisionEngineContext();
  return engine.flows.find((flow) => flow.parent_model)?.parent_model ?? null;
}
